import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader, Upload } from "lucide-react";

const modalRoot = typeof window !== "undefined" ? document.getElementById("modal-root") : null;
const apiUrl = import.meta.env.VITE_API_URL;

const InspirationModal = ({ onClose, onSuccess, editItem }) => {
    const [prompt, setPrompt] = useState(editItem?.prompt || "");
    const [image, setImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ prompt: "", image: "" });

    useEffect(() => {
        if (editItem?.image) {
            const imgUrl = editItem.image;
            setPreviewUrl(imgUrl);
        }
    }, [editItem]);

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        const newErrors = { prompt: "", image: "" };
        let valid = true;

        if (!prompt.trim()) {
            newErrors.prompt = "Prompt is required.";
            valid = false;
        } else if (prompt.trim().length < 3) {
            newErrors.prompt = "Prompt must be at least 3 characters.";
            valid = false;
        }

        if (!image && !editItem) {
            newErrors.image = "Image is required.";
            valid = false;
        }

        setErrors(newErrors);
        if (!valid) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("prompt", prompt);
            if (image) formData.append("image", image);
            if (editItem?._id) formData.append("id", editItem._id);

            const method = editItem ? "PUT" : "POST";
            const url = `${apiUrl}/api/admin-user/inspiration-images`;

            const res = await fetch(url, {
                method,
                body: formData,
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.error || "Failed");

            onSuccess(result);
        } catch (err) {
            console.error("Save failed:", err);
        } finally {
            setLoading(false);
        }
    };

    // useEffect(() => {
    //     const obj = {}
    //     if (prompt) {
    //         obj.prompt = '';
    //     }
    //     if (image) {
    //         obj.image = '';
    //     }
    //     setErrors({ ...errors, ...obj });
    // }, [image, prompt])

    if (!modalRoot) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
            <div className="bg-white rounded-lg shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto relative p-6">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-red-500">
                    <X size={20} />
                </button>

                <h2 className="text-xl font-semibold mb-4">
                    {editItem ? "Edit Inspiration" : "Create Inspiration"}
                </h2>

                <div className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Prompt</label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            className="w-full mt-1 border rounded-md p-2 text-sm"
                            rows={3}
                            placeholder="Enter prompt..."
                        />
                        {errors.prompt && <p className="text-sm text-red-600 mt-1">{errors.prompt}</p>}
                    </div>

                    <div className="max-w-60">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                        <div
                            className={`${previewUrl ? "" : "hover:border-primary-500"} relative border-2 border-dashed border-gray-300 p-2 rounded-md text-center transition`}
                        >
                            <input
                                type="file"
                                accept="image/*"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                onChange={handleImageChange}
                            />
                            {previewUrl ? (
                                <img src={previewUrl} alt="Preview" className="h-32 w-full object-cover rounded" />
                            ) : (
                                <div className="flex flex-col items-center justify-center text-gray-400">
                                    <Upload className="w-6 h-6 mb-1" />
                                    <p className="text-sm">Click or drag to upload an image</p>
                                </div>
                            )}
                        </div>
                        {errors.image && <p className="text-sm text-red-600 mt-1">{errors.image}</p>}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                    >
                        {loading && <Loader className="w-4 h-4 animate-spin" />}
                        {editItem ? "Update" : "Create"}
                    </button>
                </div>
            </div>
        </div>,
        modalRoot
    );
};

export default InspirationModal;